import * as React from 'react';
import { useCallback, useState } from 'react';
import TableList from 'common/components/TableList';
import { ListItemType } from 'common/types/charts';
import TableListItem from 'common/components/TableList/TableListItem';
import { CheckboxInput } from 'common/components/SelectPopup/styled';
import { LATANA_NAME, MODAL_LIST_HEIGHT } from 'settings/constants';
import { useGetDashboards } from 'common/queries/dashboards';
import { useChartDashboardsContext } from 'common/contexts/ChartsContext';
import { useChartInDashboard } from 'common/hooks/dashboards';
import { useRecoilValue } from 'recoil';
import { permissionsSelector } from 'common/state/account';
import { useFolders } from 'common/queries/folders';

export const useChartModalContent = () => {
  const isChartInDashboard = useChartInDashboard();
  const { data: dashboardList } = useGetDashboards();
  const { internal: isInternalUser } = useRecoilValue(permissionsSelector);
  const { updateModalState, chartsModalState, selectedChart } = useChartDashboardsContext();
  const { data: folders } = useFolders();

  const selectedChartUuid = selectedChart?.selectedChartId ?? '';

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<Array<{ id: string; name: string }>>([]);

  // Navigation handlers
  const handleNavigateToFolder = useCallback((folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setFolderPath(prev => [...prev, { id: folderId, name: folderName }]);
  }, []);

  const handleNavigateToRoot = useCallback(() => {
    setCurrentFolderId(null);
    setFolderPath([]);
  }, []);

  const handleBreadcrumbNavigation = useCallback(
    (folderId: string | null) => {
      if (folderId === null) {
        handleNavigateToRoot();
      } else {
        // Find the index of the folder in the path
        const index = folderPath.findIndex(f => f.id === folderId);
        if (index >= 0) {
          // Navigate to that specific folder
          setCurrentFolderId(folderId);
          setFolderPath(folderPath.slice(0, index + 1));
        }
      }
    },
    [folderPath, handleNavigateToRoot],
  );

  const tableList = React.useMemo(
    () => (
      <TableList
        items={dashboardList || []}
        type={ListItemType.DASHBOARDS}
        viewportOffset={MODAL_LIST_HEIGHT}
        folders={folders || []}
        subFolders={[]}
        currentFolderId={currentFolderId}
        folderPath={folderPath}
        onNavigateToFolder={handleNavigateToFolder}
        onFolderPathChange={setFolderPath}
        renderListItem={({ listItemData, ...otherProps }) => {
          const { uuid: dashboardUuid, owner } = listItemData;
          const isItemDisabled = !isInternalUser && owner?.type === LATANA_NAME;
          return (
            <TableListItem {...otherProps} {...listItemData} onChange={() => updateModalState(listItemData)} isDisabled={isItemDisabled}>
              <CheckboxInput
                readOnly
                type='checkbox'
                name={dashboardUuid}
                checked={isChartInDashboard(chartsModalState, dashboardUuid, selectedChartUuid)}
              />
            </TableListItem>
          );
        }}
      />
    ),
    [
      isChartInDashboard,
      updateModalState,
      chartsModalState,
      selectedChartUuid,
      dashboardList,
      isInternalUser,
      folders,
      currentFolderId,
      folderPath,
      handleNavigateToFolder,
    ],
  );

  // Return both the content and navigation info for breadcrumbs
  return {
    tableList,
    folderPath,
    onBreadcrumbNavigate: handleBreadcrumbNavigation,
  };
};
